import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import clsx from 'clsx'
import fileSize from 'filesize'
import ImageAddLineIcon from 'remixicon-react/ImageAddLineIcon'

import { Image } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { PopoverImagePicker } from '@tribeplatform/react-components/Picker'
import {
  useNetwork,
  usePage,
  useUpdateSpace,
  useUploadFile,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { GoogleSEOCard } from '../../../SeoCards/Google'
import { TwitterSEOCard } from '../../../SeoCards/Twitter'
import { SpaceSettingsProps } from './types'

const ImagePicker: FC<{
  thumbnailUrl: string
  onUpload: (files: Array<File>) => void
  isLoading: boolean
}> = ({ thumbnailUrl, onUpload, isLoading }) => {
  const intl = useI18n()

  const { data: network } = useNetwork()
  return (
    <div
      className="text-center mx-auto relative max-w-full border flex h-48 bg-cover"
      style={{ backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : '' }}
    >
      <div className="absolute top-0 bottom-0 start-0 end-0 cursor-pointer flex justify-center items-center">
        <PopoverImagePicker
          tabs={['upload', 'link', 'unsplash']}
          sizeConfig={{
            recommendedWidth: 1200,
            recommendedHeight: 630,
          }}
          onRemove={() => {
            onUpload(undefined)
          }}
          onImageChange={onUpload}
        >
          <div
            className={clsx(
              thumbnailUrl && 'bg-black opacity-70 rounded-md px-2 py-1',
            )}
          >
            {isLoading ? (
              <SpinnerIcon className="w-9 h-9 animate-spin" />
            ) : (
              <>
                <div className="mb-3 text-content-subdued flex justify-center">
                  <ImageAddLineIcon className="w-9 h-9" />
                </div>
                <Link>
                  <T
                    defaultMessage="Choose an image"
                    id="Generics.ChooseAnImage"
                  />
                </Link>
                <div className="text-content-subdued">
                  <T
                    defaultMessage="{formats} up to {size_limit}"
                    id="Generics.FormatAndSizeLimit"
                    values={{
                      size_limit: fileSize(
                        network?.settings?.imageSizeLimit || 10000,
                        {
                          spacer: '',
                        },
                      ),
                      formats: intl.formatList(['PNG', 'JPG', 'GIF']),
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </PopoverImagePicker>
      </div>
    </div>
  )
}

const TitleHelperText = ({ value }) => (
  <div className="flex justify-between text-content-subdued">
    <span>
      <T
        defaultMessage="Recommended length is {length, number} characters"
        id="Generics.RecommendedLength"
        values={{
          length: 60,
        }}
      />
    </span>
    <span
      className={clsx(
        'text-bold',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > 60
            ? 'text-content-on-attention'
            : 'text-content-on-positive'
          : '',
      )}
    >
      <T
        defaultMessage="Length: {length, number}"
        id="Generics.LengthIndicator"
        values={{
          length: value?.length || 0,
        }}
      />
    </span>
  </div>
)

const DescriptionHelperText = ({ value }) => (
  <div className="flex justify-between text-content-subdued">
    <span>
      <T
        defaultMessage="Recommended length is {min_length, number} to {max_length, number} characters"
        id="Generics.RecommendedLengthRange"
        values={{
          min_length: 155,
          max_length: 160,
        }}
      />
    </span>
    <span
      className={clsx(
        'text-bold',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > 160 || value.length < 155
            ? 'text-content-on-attention'
            : 'text-content-on-positive'
          : '',
      )}
    >
      <T
        defaultMessage="Length: {length, number}"
        id="Generics.LengthIndicator"
        values={{
          length: value?.length || 0,
        }}
      />
    </span>
  </div>
)

export const SpaceSeoSettings = ({
  space,
  onClose,
  isChangingTab,
  confirmTabChange,
  rejectTabChange,
  setDirty,
  setSubmit,
  isActive,
}: SpaceSettingsProps) => {
  const { $t } = useI18n()
  const { loadingFile, uploadFile } = useUploadFile()
  const { mutateAsync: updateSpace, isLoading } = useUpdateSpace()
  const { data: page } = usePage({
    variables: {
      path: space.address?.path,
    },
    useQueryOptions: {
      cacheTime: 0,
      enabled: !!space.address?.path,
    },
  })
  const buttonRef = useRef<HTMLButtonElement>()

  const initialCustomSeoDetail = useMemo(
    () => ({
      title: space.customSeoDetail.title,
      description: space.customSeoDetail.description,
      noIndex: space.customSeoDetail.noIndex,
      thumbnail: space.customSeoDetail.thumbnail as Image,
    }),
    [space],
  )

  const [customFields, setCustomFields] = useState<{
    title?: string
    description?: string
    noIndex?: boolean
    thumbnail?: Image
  }>(initialCustomSeoDetail)

  useEffect(() => {
    if (isActive) {
      setSubmit(() => () => buttonRef?.current?.click())
    }
  }, [isActive, setSubmit, buttonRef])

  useEffect(() => {
    if (isActive) {
      setDirty(customFields !== initialCustomSeoDetail)
    }
  }, [isActive, setDirty, customFields, initialCustomSeoDetail])

  const setField = useCallback(
    (key, value) => {
      setCustomFields({
        ...customFields,
        [key]: value,
      })
    },
    [customFields, setCustomFields],
  )

  const onUploadFile = useCallback(
    (files: Array<File> | undefined) => {
      if (!files) {
        setField('thumbnail', undefined)
      } else {
        uploadFile(files[0], async uploadedImage => {
          if (uploadedImage.id) {
            setField('thumbnail', uploadedImage)
          }
        })
      }
    },
    [uploadFile, setField],
  )

  const onSubmit = async () => {
    try {
      await updateSpace({
        id: space.id,
        input: {
          customSeoDetail: {
            title: customFields.title,
            description: customFields.description,
            noIndex: customFields.noIndex,
            thumbnailId: customFields.thumbnail?.id,
          },
        },
      })
      toast({
        title: $t({
          defaultMessage: '{SPACE_CC} SEO settings updated',
          id: 'Admin.Space.SeoSettings.Updated',
        }),
        status: 'success',
      })
      if (isChangingTab) {
        confirmTabChange()
      } else {
        onClose()
      }
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update {SPACE} SEO settings',
          id: 'Admin.Space.SeoSettings.FailedToUpdate',
        }),
        status: 'error',
      })
      if (isChangingTab) {
        rejectTabChange()
      }
    }
  }

  const thumbnailUrl = loadingFile?.url || customFields.thumbnail?.url
  const previewProps = {
    title: customFields.title || page?.seoDetail?.title,
    description: customFields.description || page?.seoDetail?.description,
    thumbnailUrl:
      customFields.thumbnail?.url || (page?.seoDetail?.thumbnail as Image)?.url,
  }

  return (
    <div className="sm:h-132 flex flex-col">
      <div className="flex flex-col flex-grow overflow-auto p-5">
        <SectionHeader
          title={$t({ defaultMessage: 'SEO', id: 'Generics.Seo' })}
          description={$t({
            defaultMessage:
              'Search engine optimization (SEO) allows you to improve your ranking in search results. Use these features to make it easier for users to find your page when they search for it.',
            id: 'Admin.SeoSettings.Description',
          })}
        />
        <div className="space-y-5">
          <FormControl.Input
            value={customFields.title}
            onChange={e => {
              setField('title', e.target.value)
            }}
            label={$t({
              defaultMessage: 'SEO Title',
              id: 'Admin.Space.SeoSettings.Title',
            })}
            name="title"
            placeholder={$t({
              defaultMessage:
                'If empty, we will autogenerate this based on post title',
              id: 'Admin.Space.SeoSettings.Title.Placeholder',
            })}
            helperText={<TitleHelperText value={customFields.title} />}
          />
          <FormControl.Textarea
            name="description"
            label={$t({
              defaultMessage: 'SEO Description',
              id: 'Admin.Space.SeoSettings.Description',
            })}
            placeholder={$t({
              defaultMessage:
                'If empty, we will autogenerate this based on post content',
              id: 'Admin.Space.SeoSettings.Description.Placeholder',
            })}
            value={customFields.description}
            onChange={e => {
              setField('description', e.target.value)
            }}
            helperText={
              <DescriptionHelperText value={customFields.description} />
            }
          />
          <div>
            <FormControl.Label>
              <T defaultMessage="Banner" id="Admin.Space.SeoSettings.Banner" />
            </FormControl.Label>
            <div className="mt-1">
              <ImagePicker
                onUpload={onUploadFile}
                isLoading={!!loadingFile}
                thumbnailUrl={thumbnailUrl}
              />
            </div>
            <FormControl.HelperText className="mt-2">
              <T
                defaultMessage="You can choose a custom banner for your {SPACE} to be represented on your SEO card"
                id="Admin.Space.SeoSettings.Banner.HelperText"
              />
            </FormControl.HelperText>
          </div>
          <div className="flex flex-col space-y-3">
            <p className="text-sm text-content-subdued">
              <T
                defaultMessage="Search results typically show your SEO title and description. Your title is also the browser window title. Depending on the search engine, descriptions displayed can be 50 to 300 characters long. If you don’t add a title or description, search engines will use your page title and content."
                id="Admin.Space.SeoSettings.ResultDisplayHint"
              />
            </p>
            <FormControl.Toggle
              label={$t({
                defaultMessage: 'Hide from search results',
                id: 'Admin.SeoSettings.Hide',
              })}
              name="noIndex"
              checked={customFields.noIndex}
              onChange={() => {
                setField('noIndex', !customFields.noIndex)
              }}
            />
          </div>
        </div>
        <Divider padding="none" />
        <div>
          <h6 className="text-content-subdued mb-1">
            <T
              defaultMessage="GOOGLE RESULT PREVIEW"
              id="Admin.Space.SeoSettings.GooglePreview"
            />
          </h6>
          <GoogleSEOCard {...previewProps} />
          <h6 className="text-content-subdued mt-3 mb-1">
            <T
              defaultMessage="TWITTER RESULT PREVIEW"
              id="Admin.Space.SeoSettings.TwitterPreview"
            />
          </h6>
          <TwitterSEOCard {...previewProps} />
        </div>
      </div>
      <div className="flex justify-end px-5 py-5 border-t space-s-2">
        <Button type="button" variant="outline" onClick={() => onClose()}>
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
        <Button
          type="submit"
          loading={isLoading}
          onClick={onSubmit}
          ref={buttonRef}
        >
          <T defaultMessage="Update" id="Generics.Update" />
        </Button>
      </div>
    </div>
  )
}
